import { HttpBackend, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { WebSessionRegistry } from '../../web-auth/web-session.registry';
import { isOrgApiUrl } from '../http/org-http';
import { parseOrgError } from '../models/org-errors';
import { OrgLoginRedirect } from './org-login-redirect';
import { orgBacking } from './org-session.service';

/**
 * Yalnız `<api>/v2/Org/` isteklerine org web token'ını takar. Bu istekler doğrudan HttpBackend'e gider
 * (zincirin geri kalanını atlar):
 *  - tokenInterceptor org isteğinde Authorization'ı Keycloak token'ı ile ezerdi ve 401'de Keycloak
 *    yenilemesi/çıkışı yapardı; org'un kendi 401 yönetimi burada olduğu için zincire girmesi zararlıdır.
 *  - cardV2HttpInterceptor ve paymentRequiredInterceptor da atlanır. Org uçları 402 dönmez; kota 409
 *    `QuotaExceeded` (limit/used) ile gelir ve parseOrgError ile okunur. İleride bir org ucu 402 dönerse
 *    paymentRequiredInterceptor bunu GÖRMEZ; o gün bu karar yeniden değerlendirilmelidir.
 * Org dışı istekler değişmeden geçer. Bu interceptor listede ilk sırada olmalıdır (APP_INTERCEPTORS).
 *
 * Oturum yalnız KESİN durumlarda bitirilir/giriş sayfasına gidilir: oturum yok, yenileme 401 verdi,
 * DeviceRevoked. Ağ hatası, 5xx ve yeniden denenen isteğin 403/409/428/500 gibi yanıtları olduğu gibi iletilir.
 * Giriş sayfasına gidiş OrgLoginRedirect üzerinden olur: bilinçli çıkış (signOut, keyOutdated) ya da iptal sonrası
 * "süre doldu" yönlendirmesi yapılmaz (öncelik keyOutdated > revoked > expired).
 *
 * L4/F1: org oturumu yokken kurumlu ve kurum anahtarlı ent oturumu varsa (panel QR, scp=ent) istek ent token'ıyla gider;
 * yenileme/bitirme ent yüzeyinde yapılır, giriş yönlendirmesi panel girişine olur. Org oturumu varsa her zaman o kullanılır.
 */
export const orgTokenInterceptor: HttpInterceptorFn = (req, next) => {
    if (!isOrgApiUrl(req.url)) return next(req);
    const reg = inject(WebSessionRegistry);
    const redirect = inject(OrgLoginRedirect);
    const backend = inject(HttpBackend);
    const live = orgBacking(reg);
    const surface = live?.surface === 'ent' ? 'ent' : 'org';
    // Yalnız canlı oturumda bildirilir: oturum bittikten sonraki istek, son bilinen yüzeyin girişine yönlenir.
    if (live) redirect.note(surface);

    const send = (r: HttpRequest<unknown>, token: string) =>
        backend.handle(r.clone({ headers: r.headers.delete('Authorization').set('Authorization', `Bearer ${token}`) }));

    // ENT2 (2026-10-15): yalnız sunucunun açık oturum-bitti kodu kesindir (kayıt defteri de yalnız bunlarda oturumu bitirir);
    // kodsuz 401 (ağ geçidi) oturumu kapatmaz, giriş yönlendirmesi yapılmaz.
    const isDefinitive401 = (e: unknown): boolean => e instanceof HttpErrorResponse && e.status === 401
        && ['DeviceRevoked', 'TokenExpired'].includes(parseOrgError(e).code);
    // Yenileme sırasında DeviceRevoked dönerse registry oturumu 'revoked' ile bitirmiştir; sayfa da revoked=1 ile açılır.
    const endedCause = (e: unknown): 'revoked' | 'expired' =>
        e instanceof HttpErrorResponse && parseOrgError(e).code === 'DeviceRevoked' ? 'revoked' : 'expired';

    // Yeniden denenen isteğin 401'i (Minor 4): ikinci yenileme yapılmaz. DeviceRevoked → revoked, TokenExpired → expired ile
    // oturum biter ve giriş sayfasına gidilir; öteki 401'ler ve tüm diğer hatalar olduğu gibi iletilir.
    const retry = (token: string) =>
        send(req, token).pipe(
            catchError((e: unknown) => {
                if (!(e instanceof HttpErrorResponse) || e.status !== 401) return throwError(() => e);
                const code = parseOrgError(e).code;
                if (code !== 'DeviceRevoked' && code !== 'TokenExpired') return throwError(() => e);
                const cause = code === 'DeviceRevoked' ? 'revoked' : 'expired';
                return from(reg.end(surface, cause)).pipe(
                    switchMap(() => {
                        void redirect.go(cause);
                        return throwError(() => e);
                    })
                );
            })
        );

    return from(reg.ensureFresh(surface)).pipe(
        // ensureFresh yenilerken 401 alırsa registry oturumu zaten bitirmiştir: giriş sayfasına git.
        // Ağ/5xx hatasında oturum bellekte kalır; hata iletilir, bir sonraki istek yeniden dener.
        catchError((e: unknown) => {
            if (isDefinitive401(e)) void redirect.go(endedCause(e));
            return throwError(() => e);
        }),
        switchMap((token) => {
            if (!token) {
                // Oturum yok: bilinçli bitirildiyse (signOut/keyOutdated/revoked) OrgLoginRedirect "süre doldu"yu bastırır.
                void redirect.go('expired');
                return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized', url: req.url, error: { code: 'TokenExpired' } }));
            }
            return send(req, token).pipe(
                catchError((e: unknown) => {
                    if (!(e instanceof HttpErrorResponse) || e.status !== 401) return throwError(() => e);
                    const code = parseOrgError(e).code;
                    if (code === 'DeviceRevoked') {
                        return from(reg.end(surface, 'revoked')).pipe(
                            switchMap(() => {
                                void redirect.go('revoked');
                                return throwError(() => e);
                            })
                        );
                    }
                    // Yalnız TokenExpired yenileme sebebidir; öteki 401'ler (NotOrgHolder vb.) olduğu gibi iletilir.
                    if (code !== 'TokenExpired') return throwError(() => e);
                    // Minor 5: istek gönderildikten sonra token başka bir istekle zaten yenilendiyse yeniden yenileme yapılmaz,
                    // güncel token ile bir kez yeniden denenir.
                    const current = reg.current(surface)?.token;
                    if (current && current !== token) return retry(current);
                    // Tek yenileme denemesi. Yenileme 401 verirse registry oturumu bitirmiştir (kesin son).
                    // Yenileme ağ/5xx hatası verirse oturum durur, hata iletilir. Yeniden denenen isteğin
                    // 401 dışı hatası (403/409/500...) olduğu gibi çağırana gider.
                    return from(reg.refresh(surface)).pipe(
                        catchError((e2: unknown) => {
                            if (isDefinitive401(e2)) void redirect.go(endedCause(e2));
                            return throwError(() => e2);
                        }),
                        switchMap((fresh) => retry(fresh))
                    );
                })
            );
        })
    );
};
